import { LazyLoadImage } from "react-lazy-load-image-component"
import { Redirect } from "react-router-dom"
import styled from "styled-components"

import { spacing, Text } from "@minuthq/meatball-ui-react"

import { useTaskBetaViewed } from "src/data/analytics/queries/taskManagementAnalyticsQueries"
import { useBackendFlags } from "src/data/flags/useBackendFlags"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { copyToShow } from "src/data/taskManagement/taskManagementABCopy"
import { useEffectOnce } from "src/hooks/useEffectOnce"
import { langKeys } from "src/i18n/langKeys"
import { TLangKey, useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { MButton } from "src/ui/Button/MButton"
import { Container } from "src/ui/Container/Container"
import ApartHotelBed from "src/ui/images/ai-gen-aparthotel-landscape.jpg"
import { MainView } from "src/ui/Layout/MainView"
import { BetaAccessBadge } from "src/ui/MBadge/BetaAccessBadge"

export function TaskManagementBetaStart() {
  const { t } = useTranslate()
  const { task_management_released } = useBackendFlags()
  const { orgId } = useOrganization()

  const router = useRouter()

  const taskBetaViewed = useTaskBetaViewed()

  const copy = copyToShow(orgId)

  useEffectOnce(() => {
    taskBetaViewed.mutate({
      copy,
    })
  })

  const keys = {
    A: {
      title: langKeys.task_management_beta_signup_title,
      description: langKeys.task_management_beta_signup_description_a,
      point1: langKeys.task_management_beta_signup_point_1a,
      point2: langKeys.task_management_beta_signup_point_2a,
      point3: langKeys.task_management_beta_signup_point_3a,
      button: langKeys.task_management_beta_signup_cta,
    },
    B: {
      title: langKeys.task_management_beta_signup_title,
      description: langKeys.task_management_beta_signup_description_b,
      point1: langKeys.task_management_beta_signup_point_1b,
      point2: langKeys.task_management_beta_signup_point_2b,
      point3: langKeys.task_management_beta_signup_point_3b,
      button: langKeys.task_management_beta_signup_cta,
    },
  } as const satisfies Record<"A" | "B", Record<string, TLangKey>>

  if (task_management_released) {
    return <Redirect to={Routes.TaskManagement.location().pathname} />
  }
  return (
    <MainView
      title={<Title />}
      titleBarProps={{
        description: t(langKeys.task_management_subheader),
      }}
    >
      <Container>
        <Content>
          <ImageBox src={ApartHotelBed} alt="" effect="opacity"></ImageBox>

          <TextContainer>
            <Text variant="heading2">{t(keys[copy].title)}</Text>
            <Text variant="body" color="secondary">
              {t(keys[copy].description)}
              <ul>
                <li>{t(keys[copy].point1)}</li>
                <li>{t(keys[copy].point2)}</li>
                <li>{t(keys[copy].point3)}</li>
              </ul>
            </Text>

            <ButtonContainer>
              <MButton
                onClick={() => {
                  router.navigate(Routes.TaskManagementBetaSignup.location())
                }}
                variant="primary"
              >
                {t(keys[copy].button)}
              </MButton>
            </ButtonContainer>
          </TextContainer>
        </Content>
      </Container>
    </MainView>
  )
}

function Title() {
  const { t, langKeys } = useTranslate()
  const title = t(langKeys.task, { count: 2 })
  return (
    <TitleContainer>
      <Text variant="heading1">{title}</Text>
      <BetaAccessBadge />
    </TitleContainer>
  )
}

const ButtonContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-end;
  gap: ${spacing.S};
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.S};
`

const Content = styled.div`
  display: grid;
  gap: ${spacing.L} ${spacing.XL2};

  @container (min-width: 600px) {
    grid-template-columns: 280px 1fr;
  }
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.M};
`

const ImageBox = styled(LazyLoadImage)`
  border-radius: 8px;
  object-fit: cover;
  object-position: 22%;
  /* Necessary since LazyLoadImage wraps this component in a span: */
  height: 100%;
  width: 100%;
`
